import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaPhone, FaEnvelope, FaClock, FaPaperPlane } from 'react-icons/fa';
//import { sendContactForm } from '../../services/emailService';
import { submitForm } from '../../services/postFormBackend';

const contactUsSlug = process.env.REACT_APP_FORMBACKEND_CONTACT_US_SLUG;

gsap.registerPlugin(ScrollTrigger);

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      '.contact-animate',
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
      }
    );
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      //await sendContactForm(formData);
      await submitForm(formData,contactUsSlug);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      }, 5000);
    } catch (error) {
      console.error("Error submitting form", error);
      setSubmitError("There was a problem sending your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={sectionRef} className="min-h-screen bg-white pt-32 pb-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary font-semibold px-4 py-2 rounded-full mb-6">
            <FaEnvelope className="text-secondary-600" />
            <span>Get In Touch</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6">
            We're Here to{' '}
            <span className="text-gradient">Help You</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600">
            Have questions about our loans or services? Reach out to us through any of the channels below. Our friendly team is ready to assist you.
          </p>
        </motion.div>
      </section>

      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left Column - Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="contact-animate"
          >
            <div className="bg-gradient-to-br from-primary to-primary-700 rounded-3xl p-8 md:p-10 text-white shadow-2xl relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary rounded-full blur-2xl"></div>
              </div>

              <div className="relative z-10">
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Contact Information</h2>
                <p className="text-white/80 mb-8 text-lg">Fill out the form and our team will get back to you within 24 hours.</p>
                
                <div className="space-y-6 mb-8">
                  <motion.a
                    href="tel:0468098248"
                    whileHover={{ scale: 1.05, x: 5 }}
                    className="flex items-start gap-4 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/20"
                  >
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaPhone className="text-primary-800 text-xl" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm mb-1">Phone</p>
                      <p className="text-white font-bold text-lg">0468 098 248</p>
                    </div>
                  </motion.a>
                  
                  <motion.a
                    href="mailto:info@aircashfinance.com.au"
                    whileHover={{ scale: 1.05, x: 5 }}
                    className="flex items-start gap-4 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/20"
                  >
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaEnvelope className="text-primary-800 text-xl" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm mb-1">Email</p>
                      <p className="text-white font-bold text-sm break-all">info@aircashfinance.com.au</p>
                    </div>
                  </motion.a>
                  
                  <div className="flex items-start gap-4 p-4 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20">
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaClock className="text-primary-800 text-xl" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm mb-1">Business Hours</p>
                      <p className="text-white font-semibold">Monday - Friday: 9:00 AM - 5:00 PM</p>
                      <p className="text-white/80 text-sm">Weekends: Closed</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/20">
                  <p className="text-white/70 text-sm mb-4">Available Monday - Friday, 9am - 5pm AEST</p>
                  <div className="flex items-center gap-2 text-white/60 text-xs">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Secure & Encrypted</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="contact-animate"
          >
            <div className="glass-card p-8 md:p-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-dark mb-2">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you as soon as possible.</p>
              
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-dark mb-2">Message Sent!</h3>
                    <p className="text-gray-600 mb-8">Thank you for contacting us. We'll get back to you shortly.</p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-primary hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-full transition-all duration-300"
                    >
                      Send Another Message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2" htmlFor="name">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setActiveField('name')}
                          onBlur={() => setActiveField(null)}
                          className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                            activeField === 'name'
                              ? 'border-primary focus:ring-2 focus:ring-primary/20'
                              : 'border-gray-200 focus:border-primary'
                          } focus:outline-none`}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2" htmlFor="email">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setActiveField('email')}
                          onBlur={() => setActiveField(null)}
                          className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                            activeField === 'email'
                              ? 'border-primary focus:ring-2 focus:ring-primary/20'
                              : 'border-gray-200 focus:border-primary'
                          } focus:outline-none`}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2" htmlFor="phone">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setActiveField('phone')}
                          onBlur={() => setActiveField(null)}
                          className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                            activeField === 'phone'
                              ? 'border-primary focus:ring-2 focus:ring-primary/20'
                              : 'border-gray-200 focus:border-primary'
                          } focus:outline-none`}
                          placeholder="0400 000 000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2" htmlFor="subject">
                          Subject *
                        </label>
                          <div>
  <div className="relative">
    <select
      id="subject"
      name="subject"
      value={formData.subject}
      onChange={handleChange}
      onFocus={() => setActiveField('subject')}
      onBlur={() => setActiveField(null)}
      className={`w-full px-4 py-3 pr-12 rounded-xl border-2 transition-all duration-300 bg-white appearance-none ${
        activeField === 'subject'
          ? 'border-primary focus:ring-2 focus:ring-primary/20'
          : 'border-gray-200 focus:border-primary'
      } focus:outline-none cursor-pointer`}
      required
    >
      <option value="" disabled>Please select a subject</option>
      <option value="loan-inquiry">Loan Inquiry</option>
      <option value="application-status">Application Status</option>
      <option value="general-question">General Question</option>
      <option value="feedback">Feedback</option>
      <option value="other">Other</option>
    </select>

    <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-400">
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
</div>

                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2" htmlFor="message">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setActiveField('message')}
                        onBlur={() => setActiveField(null)}
                        rows={5}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 resize-none ${
                          activeField === 'message'
                            ? 'border-primary focus:ring-2 focus:ring-primary/20'
                            : 'border-gray-200 focus:border-primary'
                        } focus:outline-none`}
                        placeholder="How can we help you?"
                        required
                      />
                    </div>
                    
                    <div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary hover:bg-primary-700 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                        <FaPaperPlane />
                      </motion.button>
                      {submitError && (
                        <p className="text-red-500 mt-3 text-sm text-center">{submitError}</p>
                      )}
                      <p className="text-gray-500 mt-4 text-sm text-center italic">
                        Your information is secure and encrypted
                      </p>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
